import { CommonModule, NgIf, NgFor, NgClass } from '@angular/common';
import { Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges, OnInit } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { AuthService } from '../../../core/services/client-services/auth.service';
import { APP_VERSION } from '../../../../environments/config';
import { SignalRService } from '../../../core/services/client-services/signalr.service';

@Component({
  selector: 'app-side-bar',
  standalone: true,
  imports: [CommonModule, NgIf, NgFor, NgClass, RouterLink, RouterLinkActive],
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.css']
})
export class SideBarComponent implements OnInit, OnChanges {
  
  appVersion = APP_VERSION
  
  @Input() collapsed = false;
  @Input() openGroupKey: string | null = null;            // <— parent drives which group to open
  @Output() expandRequest = new EventEmitter<string>();    // <— ask parent to expand

  @HostBinding('class.collapsed') get _collapsed() { return this.collapsed; }

  activeMenu: string | null = null;
  currentUrl = '';

  menuItems = [
    { label: 'Dashboard', icon: 'bi-grid-fill', route: '/dashboard' },
    {
      label: 'Clients', icon: 'bi-person-video3', groupKey: 'client',
      children: [
        { label: 'Employer', route: '/client/employer' },
        { label: 'Mobile Users', route: '/client/mobile-users' },
        { label: 'Company Employees', route: '/client/company-employees' }
      ],
    },
    {
      label: 'Credit & Risk', icon: 'bi-credit-card-2-front', groupKey: 'credit-and-risk',
      children: [
        { label: 'Manage rollovers', route: '/credit-and-risk/rollover-management' },
        { label: 'Manage Credit', route: '/credit-and-risk/credit-management' }

      ],
    },
    { label: 'Ops Risk & Compliance', icon: 'bi-clipboard-check', route: '/ops-risk-and-compliance' },
    {
      label: 'Treasury', icon: 'bi-cash-coin', groupKey: 'treasury',
      children: [
        { label: 'Cumulative Income', route: '/treasury/cumulative-income' },
        { label: 'Wage Withdrawals', route: '/treasury/wage-Withdrawals' },
        { label: 'Wallet Status', route: '/treasury/wallet-withdrawals' },
      ],
    },
    {
      label: 'Manage Payments', icon: 'bi bi-credit-card', groupKey: 'manageCixorPayments',
      children: [
        { label: 'CIXOR Payments', route: '/manageCixorPayments/cixor-payments' },
        { label: 'Payment History', route: '/manageCixorPayments/payment-history' },
        { label: 'Payroll Payments', route: '/manageCixorPayments/payroll-payments' },
      ],
    },
    {
      label: 'Fund Management', icon: 'bi bi-bank', groupKey: 'fundManagement',
      children: [
        { label: 'Contributor Management', route: '/fundManagement/contributor-management' },
        { label: 'Summary', route: '/fundManagement/summary-management' },
      ],
    },
    {
      label: 'Configuration', icon: 'bi bi-gear', groupKey: 'configuration',
      children: [
        { label: 'Late Fee Configuration', route: '/configuration/late-fee-configuration' },
        { label: 'Industry Configuration', route: '/configuration/industry-configuration' },
      ],
    },
    { label: 'User Management', icon: 'bi-person-gear', route: '/user-management' },
  ];

  constructor(
    private router: Router, 
    private authService: AuthService,
    private signalrService: SignalRService
  ) { }

  ngOnInit(): void {
    this.filterMenuItemsByAccess();
    this.setActiveMenuFromUrl(this.router.url);
    this.router.events.subscribe(e => {
      if (e instanceof NavigationEnd) this.setActiveMenuFromUrl(e.urlAfterRedirects);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['openGroupKey'] && this.openGroupKey) {
      this.activeMenu = this.openGroupKey;
    }
  }

  toggleSubMenu(groupKey: string) {
    if (this.collapsed) {                           // <-- key part
      this.expandRequest.emit(groupKey);            // ask parent to expand
      return;
    }
    this.activeMenu = this.activeMenu === groupKey ? null : groupKey;
  }

  isSubMenuOpen(groupKey: string): boolean {
    return this.activeMenu === groupKey;
  }

  private setActiveMenuFromUrl(url: string): void {
    this.currentUrl = url;
    const segments = url.split('/');
    this.activeMenu = segments.length > 1 ? segments[1] : null;
  }

  isGroupActive(group: string): boolean {
    return this.currentUrl.startsWith(`/${group}`);
  }

  logout(): void {
    this.signalrService.stopConnection();
    this.authService.logout();
    this.router.navigate(['/login']);
  }

/* =======================================================
   ✅ NEW METHOD: filter menu items by access rights
   ======================================================= */
private filterMenuItemsByAccess(): void {
  // Define mappings between menu items and access module keys
  const accessMap: Record<string, string> = {
    '/dashboard': '',
    '/client/employer': 'clients.employer',
    '/client/mobile-users': 'clients.mobileUsers',
    '/client/company-employees': 'clients.companyEmployees',
    '/credit-and-risk/rollover-management': 'creditRisk.manageRollovers',
    '/credit-and-risk/credit-management': 'creditRisk.manageCredit',
    '/ops-risk-and-compliance': 'opsRiskCompliance',
    '/treasury/cumulative-income': 'treasury.cumulativeIncome',
    '/treasury/wage-Withdrawals': 'treasury.wageWithdrawals',
    '/treasury/wallet-withdrawals': 'treasury.walletWithdrawals',
    '/user-management': 'userManagement',
    '/fundManagement/contributor-management': 'fundManagement.contributorManagement',
    '/fundManagement/summary-management': 'fundManagement.summaryManagement',
    '/manageCixorPayments/cixor-payments': 'manageCixorPayments.cixorPayments',
    '/manageCixorPayments/payment-history': 'manageCixorPayments.paymentHistory',
    '/manageCixorPayments/payroll-payments': 'manageCixorPayments.payrollPayments',
    '/configuration/late-fee-configuration': 'configuration.lateFeeConfiguration',
    '/configuration/industry-configuration': 'configuration.industryConfiguration',
  };

  // helper recursive filter
  const filterRecursive = (items: any[]): any[] => {
    return items
      .map(item => {

                if (item.route === '/dashboard') {
          return item;
        }
        if (item.children && item.children.length > 0) {
          const filteredChildren = filterRecursive(item.children);
          // Keep parent only if it has visible children
          return filteredChildren.length > 0 ? { ...item, children: filteredChildren } : null;
        } else {
          const key = accessMap[item.route];
          return key && this.authService.canView(key) ? item : null;
        }
      })
      .filter(Boolean); // remove nulls
  };

  this.menuItems = filterRecursive(this.menuItems);
}
}
